import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Badge from '@mui/material/Badge';
import {
  AppBar,
  Avatar,
  Toolbar,
  IconButton,
  Box,
  Menu,
  MenuItem,
  useTheme,
  Button,
  Typography,
} from '@mui/material';
// import NotificationsIcon from '@mui/icons-material/Notifications';
import logo from '../../resource/images/logo.png';
import { request } from './utils';
import NotificationDropdown from '../notification/notification-dropdown';
import StyledButton from './styled-components/styled-button';

const useStyles = (theme) => ({
  topContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  toolbarContainer: {
    height: '60px',
    '@media (min-width: 320px)': {
      height: '100px',
    },
  },
  logoContainer: {
    flexGrow: 1,
    userSelect: 'none',
  },
  logo: {
    color: 'white',
    width: 150,
    cursor: 'pointer',
    verticalAlign: 'middle',
    marginLeft: 2,
    marginBottom: '5px', // The logo is not vertically centered without this
    [theme.breakpoints.up('sm')]: {
      width: 200,
    },
  },
  vendorDashboardButton: {
    color: 'white',
  },
  becomeVendorButton: {
    color: 'white',
  },
  navbarIcon: {
    color: '#FFFFFF',
    paddingRight: 1.5,
  },
  aboutMenuItem: {
    paddingLeft: 4,
    paddingRight: 8,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      transform: 'scale(1.05)',
      cursor: 'pointer',
    },
  },
  accountMenuItem: {
    paddingLeft: 4,
    paddingRight: 8,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      transform: 'scale(1.05)',
      cursor: 'pointer',
    },
  },
  accountMenuAvatar: {
    height: '36px',
    width: '36px',
    color: theme.palette.primary.main,
    bgcolor: 'white',
  },
  accountMenuheader: {
    width: '100%',
    paddingLeft: 4,
    paddingRight: 4,
    marginTop: 2,
    marginBottom: 2,
    paddingBottom: 2,
    borderBottom: '1px solid #ccc',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  accountMenuHeaderAvatar: {
    height: '36px',
    width: '36px',
  },
  accountMenuHeaderName: {
    marginLeft: 1,
  },
});

function NavBarDesktop(props) {
  const {
    accountMenuAnchorEl,
    onClickAccount,
    closeAccountMenu,
    openSignInDialog,
    openSignUpDialog,
    handleLogout,
    user,
    // unreadNotificationCount,
    unreadChatCount,
    cartItemCount,
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = useStyles(theme);

  const isVendorPage = location.pathname.startsWith('/v/');
  const isVendor = user && user.id && user.role === 'vendor';

  const [notifications, setNotifications] = useState([]);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const isNotificationDropdownOpen = Boolean(notificationAnchorEl);

  const [aboutAnchorEl, setAboutAnchorEl] = useState(null);
  const isAboutOpen = Boolean(aboutAnchorEl);

  // const handleNotificationClick = (event) => {
  //   setNotificationAnchorEl(event.currentTarget);
  // };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleAboutMenuClick = (event) => {
    setAboutAnchorEl(event.currentTarget);
  };

  const handleAboutMenuClose = () => {
    setAboutAnchorEl(null);
  };

  // Function to fetch notifications
  const fetchNotifications = () => {
    request('/get-push-notifications', 'GET')
      .then((res) => {
        setNotifications(res.data); // Assuming 'data' contains the notifications
      })
      .catch((err) => {
        console.error('Error fetching notifications:', err);
      });
  };

  useEffect(() => {
    console.log(user);
    if (isNotificationDropdownOpen) {
      fetchNotifications();
    }
  }, [isNotificationDropdownOpen]);

  const redirectToHome = () => {
    navigate('/');
  };

  const redirectToChat = () => {
    navigate('/chat');
  };

  const redirectToCart = () => {
    navigate('/cart');
  };

  const redirectToVendorSignup = () => {
    if (user && user.id) {
      navigate('/v/vendor-signup');
    } else {
      navigate('/signup?redirectUrl=/v/vendor-signup');
    }
  };

  const redirectToVendorDashboard = () => {
    navigate('/v/vendor-dashboard');
  };

  const redirectToAboutUs = () => {
    handleAboutMenuClose();
    navigate('/about-rentovault');
  };

  const redirectToFAQ = () => {
    handleAboutMenuClose();
    navigate('/faq');
  };

  const redirectToTC = () => {
    handleAboutMenuClose();
    navigate('/terms-and-conditions');
  };

  const redirectToContact = () => {
    handleAboutMenuClose();
    navigate('/contact');
  };

  const redirectToSelfProfile = () => {
    closeAccountMenu();
    navigate('/self-profile');
  };

  const redirectToOrders = () => {
    closeAccountMenu();
    navigate('/orders');
  };

  return (
    <Box sx={styles.topContainer}>
      <AppBar position="static">
        <Toolbar sx={styles.toolbarContainer}>
          <Box component="div" sx={styles.logoContainer}>
            <Box
              component="img"
              src={logo}
              alt="Rentovault"
              sx={styles.logo}
              onClick={redirectToHome}
            />
          </Box>

          <Button
            variant="text"
            onClick={handleAboutMenuClick}
            sx={{ color: 'white', textTransform: 'none', marginRight: 1 }}
            endIcon={<ArrowDropDownIcon />}
          >
            About
          </Button>
          <Menu
            anchorEl={aboutAnchorEl}
            open={isAboutOpen}
            onClose={handleAboutMenuClose}
          >
            <MenuItem sx={styles.aboutMenuItem} onClick={redirectToAboutUs}>
              About Rentovault
            </MenuItem>
            <MenuItem sx={styles.aboutMenuItem} onClick={redirectToFAQ}>
              FAQ
            </MenuItem>
            <MenuItem sx={styles.aboutMenuItem} onClick={redirectToTC}>
              Terms and Conditions
            </MenuItem>
            <MenuItem sx={styles.aboutMenuItem} onClick={redirectToContact}>
              Contact
            </MenuItem>
          </Menu>

          {
            !isVendorPage && (
              isVendor ? (
                <StyledButton
                  variant="text"
                  label="Vendor Dashboard"
                  sx={styles.vendorDashboardButton}
                  onClick={redirectToVendorDashboard}
                />
              ) : (
                <StyledButton
                  variant="text"
                  label="Become A Vendor"
                  sx={styles.becomeVendorButton}
                  onClick={redirectToVendorSignup}
                />
              )
            )
          }

          {/* { user && user.id && (
            <IconButton sx={styles.navbarIcon} onClick={handleNotificationClick}>
              <Badge badgeContent={unreadNotificationCount} color="warning">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )} */}

          { user && user.id && (
            <IconButton sx={styles.navbarIcon} onClick={redirectToChat}>
              <Badge badgeContent={unreadChatCount} color="warning">
                <ChatRoundedIcon />
              </Badge>
            </IconButton>
          )}

          { user && user.id && (
            <IconButton sx={styles.navbarIcon} onClick={redirectToCart}>
              <Badge badgeContent={cartItemCount} color="warning">
                <ShoppingCartIcon />
              </Badge>
            </IconButton>
          )}

          <Button
            color="inherit"
            onClick={onClickAccount}
            sx={{ minWidth: 0, padding: 0 }}
          >
            { user && user.id ? (
              <Avatar
                sx={styles.accountMenuAvatar}
              >
                {user.first_name.charAt(0)}
              </Avatar>
            ) : (
              <AccountCircleIcon />
            )}
            <ArrowDropDownIcon fontSize="small" sx={{ ml: 0.3 }} />
          </Button>
          <Menu
            anchorEl={accountMenuAnchorEl}
            open={Boolean(accountMenuAnchorEl)}
            onClose={closeAccountMenu}
          >
            { user && user.id ? (
              <>
                <Box sx={styles.accountMenuheader}>
                  <Avatar
                    sx={styles.accountMenuHeaderAvatar}
                    src={user.profile_picture ? user.profile_picture.image : null}
                  >
                    {user.first_name.charAt(0)}
                  </Avatar>
                  <Typography variant="h6" sx={styles.accountMenuHeaderName}>
                    {`${user.first_name} ${user.last_name}`}
                  </Typography>
                </Box>
                <MenuItem sx={styles.accountMenuItem} onClick={redirectToSelfProfile}>
                  Profile
                </MenuItem>
                <MenuItem sx={styles.accountMenuItem} onClick={redirectToOrders}>
                  Orders
                </MenuItem>
                <MenuItem sx={styles.accountMenuItem} onClick={handleLogout}>
                  Logout
                </MenuItem>
              </>
            ) : (
              <>
                <MenuItem sx={styles.accountMenuItem} onClick={openSignInDialog}>
                  Login
                </MenuItem>
                <MenuItem sx={styles.accountMenuItem} onClick={openSignUpDialog}>
                  Signup
                </MenuItem>
              </>
            )}
          </Menu>
        </Toolbar>
      </AppBar>
      <NotificationDropdown
        anchorEl={notificationAnchorEl}
        open={isNotificationDropdownOpen}
        onClose={handleNotificationClose}
        notifications={notifications}
        setNotifications={setNotifications}
      />
    </Box>
  );
}

NavBarDesktop.propTypes = {
  accountMenuAnchorEl: PropTypes.oneOfType([PropTypes.elementType]),
  onClickAccount: PropTypes.func.isRequired,
  closeAccountMenu: PropTypes.func.isRequired,
  openSignInDialog: PropTypes.func.isRequired,
  openSignUpDialog: PropTypes.func.isRequired,
  handleLogout: PropTypes.func.isRequired,
  user: PropTypes.oneOfType([PropTypes.object]).isRequired,
  // unreadNotificationCount: PropTypes.number,
  unreadChatCount: PropTypes.number.isRequired,
  cartItemCount: PropTypes.number,
};

NavBarDesktop.defaultProps = {
  // unreadNotificationCount: 0,
  cartItemCount: 0,
  accountMenuAnchorEl: null,
};

export default NavBarDesktop;
